import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Plus, Minus, Check, Trash2, Clock, Info, UtensilsCrossed,
  Sparkles, ChefHat, CalendarDays, ShoppingBag, PartyPopper, MousePointerClick,
} from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const LUNCH_IMG = "/assets/products/spaghetti-viande.webp";
const BUFFET_IMG = "/assets/products/assiette-pique-nique.webp";
const isTouch = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const PLATTERS = {
  "Bouchées": "/assets/buffet/bouchees.jpeg",
  "Salades": "/assets/buffet/salades.jpeg",
  "Pâtés": "/assets/buffet/pates.jpeg",
  "Suppléments": "/assets/buffet/supplements.jpeg",
  "Service": "/assets/buffet/service.jpeg",
};
const platterFor = (cat) => PLATTERS[cat] || "/assets/buffet/default.jpeg";

const DAYS = [
  { id: "lun", label: "Lundi" },
  { id: "mar", label: "Mardi" },
  { id: "mer", label: "Mercredi" },
  { id: "jeu", label: "Jeudi" },
  { id: "ven", label: "Vendredi" },
];

/* ============================ PAGE ============================ */
export default function Buffets() {
  const [mode, setMode] = useState("table");
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="mb-8 animate-fade-up">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-border text-sm text-coral font-medium mb-4">
          <ChefHat className="h-4 w-4" /> Studio Buffet · sur mesure
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-foreground leading-[1.05]">Composez votre événement</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Déposez vos plats sur la table (ou touchez pour ajouter). Le devis se calcule en temps réel, selon nos vraies formules — sans surprise.
        </p>
      </div>

      <div className="inline-flex p-1 rounded-2xl bg-ink-800 border border-border mb-8" data-testid="buffet-mode-switch">
        <ModeBtn active={mode === "table"} onClick={() => setMode("table")} icon={PartyPopper} label="Table Buffet" testid="mode-table" />
        <ModeBtn active={mode === "lunch"} onClick={() => setMode("lunch")} icon={CalendarDays} label="Boîtes à lunch" testid="mode-lunch" />
      </div>

      {mode === "table" ? <TableBuffet /> : <Lunchbox />}
    </div>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-heading text-sm font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

/* ============================ TABLE BUFFET ============================ */
function TableBuffet() {
  const { addItem } = useCart();
  const { data } = useQuery({ queryKey: ["buffets"], queryFn: async () => (await api.get("/buffets")).data });
  const templates = data?.templates || [];
  const addonsCatalog = data?.addons || [];

  const [templateId, setTemplateId] = useState(null);
  const [guests, setGuests] = useState(20);
  const [choices, setChoices] = useState({});
  const [addons, setAddons] = useState({});
  const [quote, setQuote] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const debounce = useRef(null);

  const template = useMemo(() => templates.find((t) => t._id === templateId), [templates, templateId]);

  useEffect(() => {
    if (!templateId && templates.length) { setTemplateId(templates[0]._id); setGuests(templates[0].min_guests); }
  }, [templates, templateId]);

  const selectTemplate = (t) => { setTemplateId(t._id); setChoices({}); setGuests((g) => Math.max(g, t.min_guests)); };

  useEffect(() => {
    if (!templateId) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const res = await api.post("/buffet/quote", {
          template_id: templateId, guests, choices,
          addons: Object.entries(addons).map(([id, qty]) => ({ id, qty })),
        });
        setQuote(res.data);
      } catch { /* ignore */ }
    }, 180);
    return () => clearTimeout(debounce.current);
  }, [templateId, guests, choices, addons]);

  const addAddon = useCallback((id) => {
    const a = addonsCatalog.find((x) => x._id === id);
    if (!a) return;
    setAddons((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
    toast.success(`${a.nom} déposé sur la table`);
  }, [addonsCatalog]);

  const setAddonQty = (id, qty) => setAddons((prev) => {
    const c = { ...prev }; if (qty <= 0) delete c[id]; else c[id] = qty; return c;
  });

  const addonGroups = useMemo(() => {
    const g = {}; addonsCatalog.forEach((a) => { (g[a.cat] ||= []).push(a); }); return g;
  }, [addonsCatalog]);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const id = e.dataTransfer.getData("text/plain"); if (id) addAddon(id);
  };

  const addToCart = () => {
    if (!quote || !quote.eligible) return;
    addItem({
      product_id: `buffet-${templateId}-${Date.now()}`, kind: "buffet", configurable: true,
      nom: `${template.title} · ${guests} invités`, prix: quote.subtotal, quantity: 1, image: BUFFET_IMG,
      options: quote.lines.map((l) => ({ nom: l.label })),
      note: quote.service ? "Buffet avec service (frais + 12% pourboire)" : "",
    });
  };

  if (!template) return <div className="h-64 rounded-2xl bg-ink-800 animate-pulse" />;

  // platters currently on the table = included items + dropped addons
  const includedPlatters = template.included.map((nom, i) => ({ key: `inc-${i}`, img: platterFor(guessCat(nom)), label: nom, included: true }));
  const addonPlatters = Object.entries(addons).map(([id, qty]) => {
    const a = addonsCatalog.find((x) => x._id === id) || {};
    return { key: id, id, img: platterFor(a.cat), label: a.nom, qty, cat: a.cat };
  });

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-8" data-testid="table-buffet">
      <div className="space-y-8">
        {/* Step 1 — formula */}
        <Section n={1} title="Choisissez votre formule">
          <div className="grid sm:grid-cols-2 gap-4">
            {templates.map((t) => (
              <button key={t._id} data-testid={`formula-${t._id}`} onClick={() => selectTemplate(t)}
                className={`text-left p-5 rounded-2xl border transition-colors ${templateId === t._id ? "border-coral bg-coral/5 ring-1 ring-coral" : "border-border bg-ink-800 hover:border-coral/40"}`}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-heading font-bold text-foreground">{t.title}</h3>
                  {templateId === t._id && <Check className="h-5 w-5 text-coral shrink-0" />}
                </div>
                <p className="text-2xl font-heading font-extrabold text-coral mt-1">{money(t.price_per_person)}<span className="text-sm text-muted-foreground font-normal">/pers</span></p>
                <p className="text-xs text-muted-foreground mt-1">{t.tagline}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Pill icon={Users}>{t.min_guests} pers min.</Pill>
                  {t.service && <Pill icon={UtensilsCrossed}>Avec service</Pill>}
                </div>
              </button>
            ))}
          </div>
        </Section>

        {/* Step 2 — guests */}
        <Section n={2} title="Nombre d'invités">
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center rounded-full bg-ink-800 border border-border">
              <StepBtn onClick={() => setGuests((g) => Math.max(1, g - 5))} testid="guests-minus"><Minus className="h-4 w-4" /></StepBtn>
              <input type="number" value={guests} data-testid="guests-input"
                onChange={(e) => setGuests(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 text-center bg-transparent font-heading text-2xl font-bold text-foreground focus:outline-none" />
              <StepBtn onClick={() => setGuests((g) => g + 5)} testid="guests-plus"><Plus className="h-4 w-4" /></StepBtn>
            </div>
            <span className="text-sm text-muted-foreground">invités</span>
          </div>
          {guests < template.min_guests && (
            <p data-testid="guests-warning" className="mt-3 text-sm text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 inline-flex items-center gap-2">
              <Info className="h-4 w-4" /> Minimum {template.min_guests} personnes pour « {template.title} ».
            </p>
          )}
        </Section>

        {/* choices */}
        {template.choices?.length > 0 && (
          <Section n={3} title="Personnalisez">
            {template.choices.map((ch) => {
              const opts = [
                ...ch.options.map((o) => ({ name: o, extra: 0 })),
                ...Object.entries(ch.extras || {}).map(([o, p]) => ({ name: o, extra: p })),
              ];
              return (
                <div key={ch.label}>
                  <p className="font-heading text-sm font-semibold text-foreground mb-2">{ch.label}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {opts.map((o) => (
                      <button key={o.name} data-testid={`choice-${o.name}`}
                        onClick={() => setChoices((prev) => ({ ...prev, [ch.label]: o.name }))}
                        className={`flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-left text-sm transition-colors ${choices[ch.label] === o.name ? "border-coral bg-coral/5 ring-1 ring-coral" : "border-border bg-ink-800 hover:border-coral/40"}`}>
                        <span className="text-foreground">{o.name}</span>
                        {o.extra > 0 && <span className="text-xs text-coral font-bold">+{money(o.extra)}/pers</span>}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </Section>
        )}

        {/* THE 3D TABLE */}
        <Section n={template.choices?.length > 0 ? 4 : 3} title="Dressez votre table"
          hint={isTouch ? "Touchez un plat plus bas pour le poser sur la table." : "Glissez un plat sur la table (ou cliquez « Ajouter »)."}>
          <div style={{ perspective: "1400px" }} className="pt-4">
            <motion.div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="buffet-table"
              animate={{ rotateX: 16, boxShadow: dragOver ? "0 40px 80px -20px rgba(226,72,27,0.45)" : "0 40px 70px -25px rgba(0,0,0,0.6)" }}
              transition={{ type: "spring", stiffness: 120, damping: 18 }}
              style={{ transformOrigin: "center bottom" }}
              className={`relative mx-auto max-w-3xl rounded-[36px] px-6 pt-14 pb-10 min-h-[240px] border ${dragOver ? "border-coral" : "border-[#c9b79a]/40"}`}
            >
              {/* linen surface */}
              <div className="absolute inset-0 rounded-[36px] overflow-hidden" style={{
                background: "repeating-linear-gradient(135deg, #efe6d6 0px, #efe6d6 14px, #e9dfcc 14px, #e9dfcc 28px)",
              }}>
                <div className="absolute inset-0" style={{ background: "radial-gradient(120% 90% at 50% 20%, rgba(255,255,255,0.5), transparent 60%)" }} />
                {dragOver && <div className="absolute inset-0 bg-coral/15 animate-pulse" />}
              </div>
              {/* front edge (thickness) */}
              <div className="absolute -bottom-3 inset-x-3 h-4 rounded-b-2xl bg-[#c9b79a]" style={{ filter: "brightness(0.85)" }} />

              {dragOver && (
                <div className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 z-20">
                  <span className="px-4 py-2 rounded-full bg-coral text-white text-sm font-heading font-bold shadow-lg">Déposez ici ✦</span>
                </div>
              )}

              {/* platters lying on the table (upright, generous spacing → no overlap) */}
              <div className="relative z-10 flex flex-wrap justify-center gap-x-6 gap-y-9">
                <AnimatePresence mode="popLayout">
                  {includedPlatters.map((p) => <Platter key={p.key} img={p.img} label={p.label} included />)}
                  {addonPlatters.map((p) => (
                    <Platter key={p.key} id={p.id} img={p.img} label={p.label} qty={p.qty}
                      onRemove={() => setAddonQty(p.id, 0)}
                      onInc={() => setAddonQty(p.id, p.qty + 1)} onDec={() => setAddonQty(p.id, p.qty - 1)} />
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </Section>

        {/* Palette */}
        <Section title="Bouchées & extras" hint="Faites glisser vers la table">
          <div className="space-y-5">
            {Object.entries(addonGroups).map(([cat, list]) => (
              <div key={cat}>
                <p className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <img src={platterFor(cat)} alt="" className="h-6 w-6 rounded-full object-cover" /> {cat}
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {list.map((a) => (
                    <motion.div key={a._id} whileHover={{ y: -2 }} whileDrag={{ scale: 1.06, rotate: -3, zIndex: 50 }}
                      draggable={!isTouch} onDragStart={(e) => e.dataTransfer.setData("text/plain", a._id)}
                      data-testid={`palette-${a._id}`}
                      className="group flex items-center gap-2 p-2.5 rounded-xl bg-ink-800 border border-border hover:border-coral/40 transition-colors cursor-grab active:cursor-grabbing">
                      <img src={platterFor(a.cat)} alt="" className="h-11 w-11 rounded-full object-cover shrink-0 shadow" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground truncate">{a.nom}</p>
                        <p className="text-xs text-coral font-bold">{money(a.prix)} {a.unit === "pers" ? "/pers" : "/unité"}</p>
                      </div>
                      <button onClick={() => addAddon(a._id)} data-testid={`palette-add-${a._id}`}
                        className="h-8 w-8 rounded-full bg-primary/90 text-primary-foreground flex items-center justify-center hover:opacity-90 shrink-0" aria-label={`Ajouter ${a.nom}`}>
                        <Plus className="h-4 w-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Live quote */}
      <aside className="lg:sticky lg:top-24 self-start">
        <div className="rounded-2xl bg-ink-800 border border-border p-5" data-testid="buffet-quote">
          <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2"><Sparkles className="h-5 w-5 text-coral" /> Votre devis</h3>
          <div className="mt-4 space-y-2 max-h-[40vh] overflow-y-auto pr-1">
            {(quote?.lines || []).map((l, i) => (
              <div key={i} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-muted-foreground">{l.label}</span>
                <span className="text-foreground font-medium whitespace-nowrap">{money(l.amount)}</span>
              </div>
            ))}
          </div>
          <div className="h-px bg-border my-4" />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Sous-total</span>
            <span className="font-heading text-2xl font-extrabold text-foreground" data-testid="quote-subtotal">{money(quote?.subtotal || 0)}</span>
          </div>
          {quote?.service_note && <p className="text-xs text-muted-foreground mt-2 flex gap-1.5"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-coral" /> {quote.service_note}</p>}
          <p className="text-xs text-muted-foreground mt-2 flex gap-1.5"><Clock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-coral" /> Délai minimum : {quote?.lead_time_hours || 48} h avant l'événement.</p>
          {quote && !quote.eligible && <p data-testid="quote-blocked" className="mt-3 text-sm text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2">{quote.messages[0]}</p>}
          <button onClick={addToCart} disabled={!quote?.eligible} data-testid="buffet-add-cart"
            className="mt-4 w-full py-3.5 rounded-xl bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground font-heading font-semibold transition-colors active:scale-[0.98] flex items-center justify-center gap-2">
            <ShoppingBag className="h-4 w-4" /> Ajouter au panier
          </button>
        </div>
      </aside>
    </div>
  );
}

function Platter({ id, img, label, qty = 1, included, onRemove, onInc, onDec }) {
  return (
    <motion.div layout
      initial={{ scale: 0.3, y: -50, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      exit={{ scale: 0.3, opacity: 0, transition: { duration: 0.18 } }}
      transition={{ type: "spring", stiffness: 420, damping: 14 }}
      data-testid={id ? `table-addon-${id}` : "table-included"}
      className="group relative flex flex-col items-center w-[104px]"
    >
      <div className="relative">
        {/* elliptical shadow on the linen */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-20 h-3 rounded-[50%] bg-black/25 blur-[3px]" />
        <img src={img} alt={label} className="w-[92px] h-[92px] rounded-full object-cover border-4 border-white shadow-xl" style={{ transform: "scaleY(0.9)" }} />
        {qty > 1 && <span className="absolute -top-1 -right-1 h-6 min-w-6 px-1 rounded-full bg-coral text-white text-xs font-bold flex items-center justify-center shadow">×{qty}</span>}
        {!included && (
          <button onClick={onRemove} data-testid={`platter-remove-${id}`} className="absolute -top-1 -left-1 h-6 w-6 rounded-full bg-white text-red-500 shadow flex items-center justify-center opacity-90 md:opacity-0 md:group-hover:opacity-100 transition-opacity" aria-label="Retirer">
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
      <span className="mt-2 text-[11px] leading-tight text-center text-[#4a3f33] font-medium max-w-[104px] line-clamp-2">{label}</span>
      {!included && (
        <div className="mt-1 inline-flex items-center rounded-full bg-white/80 border border-[#c9b79a]/50 shadow-sm">
          <button onClick={onDec} data-testid={`platter-dec-${id}`} className="h-6 w-6 flex items-center justify-center text-[#6E2637]"><Minus className="h-3 w-3" /></button>
          <span className="w-5 text-center text-xs font-bold text-[#4a3f33]">{qty}</span>
          <button onClick={onInc} data-testid={`platter-inc-${id}`} className="h-6 w-6 flex items-center justify-center text-[#6E2637]"><Plus className="h-3 w-3" /></button>
        </div>
      )}
    </motion.div>
  );
}

// rough mapping of included item labels to a platter category
function guessCat(nom) {
  const n = nom.toLowerCase();
  if (n.includes("salade") || n.includes("chou") || n.includes("crudité")) return "Salades";
  if (n.includes("pâté")) return "Pâtés";
  if (n.includes("ustensile") || n.includes("couvert") || n.includes("café") || n.includes("essuie")) return "Service";
  if (n.includes("dessert") || n.includes("fromage") || n.includes("olive") || n.includes("œuf") || n.includes("oeuf")) return "Suppléments";
  return "Bouchées";
}

/* ============================ LUNCHBOX ============================ */
function Lunchbox() {
  const { addItem } = useCart();
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const [boxes, setBoxes] = useState(() => Object.fromEntries(DAYS.map((d) => [d.id, []])));
  const [dragDay, setDragDay] = useState(null);
  const [picker, setPicker] = useState(null); // productId whose day-picker is open

  const addToDays = useCallback((productId, dayIds) => {
    const p = products.find((x) => x.id === productId);
    if (!p || !dayIds.length) return;
    setBoxes((prev) => {
      const next = { ...prev };
      dayIds.forEach((dayId) => {
        const list = [...next[dayId]];
        const idx = list.findIndex((i) => i.id === p.id);
        if (idx >= 0) list[idx] = { ...list[idx], qty: list[idx].qty + 1 };
        else list.push({ id: p.id, nom: p.nom, prix: p.prix, image: p.image, qty: 1 });
        next[dayId] = list;
      });
      return next;
    });
    toast.success(`${p.nom} ajouté à ${dayIds.length} jour(s)`);
  }, [products]);

  const setQty = (dayId, id, qty) => setBoxes((prev) => {
    let list = prev[dayId];
    if (qty <= 0) list = list.filter((i) => i.id !== id);
    else list = list.map((i) => (i.id === id ? { ...i, qty } : i));
    return { ...prev, [dayId]: list };
  });

  const weekTotal = useMemo(() => Object.values(boxes).flat().reduce((s, i) => s + i.prix * i.qty, 0), [boxes]);
  const itemCount = useMemo(() => Object.values(boxes).flat().reduce((s, i) => s + i.qty, 0), [boxes]);

  const addToCart = () => {
    if (itemCount === 0) return;
    const summary = DAYS.filter((d) => boxes[d.id].length)
      .map((d) => ({ nom: `${d.label} — ${boxes[d.id].map((i) => `${i.nom}${i.qty > 1 ? ` ×${i.qty}` : ""}`).join(", ")}` }));
    addItem({
      product_id: `lunchbox-${Date.now()}`, kind: "lunchbox", configurable: true,
      nom: `Boîtes à lunch · semaine (${itemCount} items)`, prix: Number(weekTotal.toFixed(2)),
      quantity: 1, image: LUNCH_IMG, options: summary,
    });
  };

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-8" data-testid="lunchbox">
      <div className="space-y-8">
        <Section title="Votre semaine" hint={isTouch ? "Touchez « + » sur un plat, cochez les jours puis confirmez." : "Glissez un plat sur une boîte (le couvercle s'ouvre) — ou utilisez « + »."}>
          <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
            {DAYS.map((d) => (
              <LunchBoxCard key={d.id} day={d} items={boxes[d.id]} isOpen={dragDay === d.id} active={boxes[d.id].length > 0}
                onDragOver={(e) => { e.preventDefault(); setDragDay(d.id); }}
                onDragLeave={() => setDragDay((v) => (v === d.id ? null : v))}
                onDrop={(e) => { e.preventDefault(); setDragDay(null); const id = e.dataTransfer.getData("text/plain"); if (id) addToDays(id, [d.id]); }}
                onQty={(id, q) => setQty(d.id, id, q)} />
            ))}
          </div>
        </Section>

        <Section title="Nos plats" hint="Glissez-les, ou cliquez « + » pour choisir plusieurs jours">
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {products.map((p) => (
              <motion.div key={p.id} whileHover={{ y: -2 }} whileDrag={{ scale: 1.06, rotate: -3, zIndex: 50 }}
                draggable={!isTouch} onDragStart={(e) => e.dataTransfer.setData("text/plain", p.id)}
                data-testid={`lunch-palette-${p.id}`}
                className="relative flex items-center gap-3 p-2.5 rounded-xl bg-ink-800 border border-border hover:border-coral/40 transition-colors cursor-grab active:cursor-grabbing">
                <img src={p.image} alt={p.nom} className="h-12 w-12 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{p.nom}</p>
                  <p className="text-xs text-coral font-bold">{money(p.prix)}</p>
                </div>
                <button onClick={(e) => setPicker(picker?.id === p.id ? null : { id: p.id, rect: e.currentTarget.getBoundingClientRect() })} data-testid={`lunch-add-${p.id}`}
                  className="h-8 w-8 rounded-full bg-primary/90 text-primary-foreground flex items-center justify-center hover:opacity-90 shrink-0" aria-label="Choisir les jours">
                  <Plus className="h-4 w-4" />
                </button>
              </motion.div>
            ))}
          </div>
        </Section>

        {picker && (
          <DayPicker product={products.find((x) => x.id === picker.id)} rect={picker.rect} boxes={boxes}
            onClose={() => setPicker(null)} onConfirm={(days) => { addToDays(picker.id, days); setPicker(null); }} />
        )}
      </div>

      <aside className="lg:sticky lg:top-24 self-start">
        <div className="rounded-2xl bg-ink-800 border border-border p-5" data-testid="lunchbox-summary">
          <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2"><CalendarDays className="h-5 w-5 text-coral" /> Résumé semaine</h3>
          <div className="mt-4 space-y-2">
            {DAYS.map((d) => {
              const n = boxes[d.id].reduce((s, i) => s + i.qty, 0);
              return (
                <div key={d.id} className={`flex items-center justify-between text-sm px-2 py-1 rounded-lg ${n > 0 ? "bg-coral/10 text-foreground" : ""}`}>
                  <span className={n > 0 ? "text-foreground font-medium" : "text-muted-foreground"}>{d.label}</span>
                  <span className={n > 0 ? "text-coral font-bold" : "text-muted-foreground"}>{n} item(s)</span>
                </div>
              );
            })}
          </div>
          <div className="h-px bg-border my-4" />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Total</span>
            <span className="font-heading text-2xl font-extrabold text-foreground" data-testid="lunchbox-total">{money(weekTotal)}</span>
          </div>
          <button onClick={addToCart} disabled={itemCount === 0} data-testid="lunchbox-add-cart"
            className="mt-4 w-full py-3.5 rounded-xl bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground font-heading font-semibold transition-colors active:scale-[0.98] flex items-center justify-center gap-2">
            <ShoppingBag className="h-4 w-4" /> Ajouter au panier
          </button>
        </div>
      </aside>
    </div>
  );
}

function LunchBoxCard({ day, items, isOpen, active, onDragOver, onDragLeave, onDrop, onQty }) {
  const count = items.reduce((s, i) => s + i.qty, 0);
  return (
    <div style={{ perspective: "700px" }}>
      <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} data-testid={`lunchbox-day-${day.id}`}
        className="relative" style={{ transformStyle: "preserve-3d" }}>
        {/* Lid */}
        <motion.div
          animate={{ rotateX: isOpen ? -115 : 0, backgroundColor: isOpen ? "#E2481B" : active ? "#6E2637" : "#241b16" }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          style={{ transformOrigin: "top center", transformStyle: "preserve-3d" }}
          className="relative z-20 rounded-t-2xl border border-b-0 border-[#c9b79a]/30 px-3 py-3 text-center shadow-lg"
        >
          <p className="font-heading text-sm font-bold text-white flex items-center justify-center gap-1.5">
            {day.label} {active && !isOpen && <Check className="h-3.5 w-3.5" />}
          </p>
          {isOpen && <span className="text-[10px] text-white/90 uppercase tracking-widest">Déposez</span>}
        </motion.div>
        {/* Body */}
        <div className={`relative z-10 rounded-b-2xl border-2 p-2.5 min-h-[150px] -mt-0.5 transition-colors ${isOpen ? "border-coral bg-coral/10" : active ? "border-coral/60 bg-ink-800" : "border-border bg-ink-800"}`}>
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground pt-2">Boîte vide</p>
          ) : (
            <div className="space-y-1.5 pt-1">
              <AnimatePresence>
                {items.map((i) => (
                  <motion.div key={i.id} layout initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ type: "spring", stiffness: 400, damping: 18 }}
                    data-testid={`lunchbox-item-${day.id}-${i.id}`} className="flex items-center gap-1.5 text-xs bg-foreground/5 rounded-lg p-1.5">
                    <img src={i.image} alt="" className="h-6 w-6 rounded object-cover shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-foreground">{i.nom}</span>
                    <button onClick={() => onQty(i.id, i.qty - 1)} className="h-5 w-5 rounded bg-ink-900 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                    <span className="w-4 text-center font-bold">{i.qty}</span>
                    <button onClick={() => onQty(i.id, i.qty + 1)} className="h-5 w-5 rounded bg-ink-900 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
          {count > 0 && <span className="absolute -top-2 -right-2 h-6 min-w-6 px-1 rounded-full bg-coral text-white text-xs font-bold flex items-center justify-center shadow z-30">{count}</span>}
        </div>
      </div>
    </div>
  );
}

function DayPicker({ product, rect, boxes, onConfirm, onClose }) {
  const [checked, setChecked] = useState(() => new Set(DAYS.filter((d) => boxes[d.id].some((i) => i.id === product?.id)).map((d) => d.id)));
  if (!product || !rect) return null;
  const toggle = (id) => setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const top = Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - 360));
  const left = Math.max(8, Math.min(rect.right - 208, window.innerWidth - 216));
  return createPortal(
    <>
      <div className="fixed inset-0 z-[100]" onClick={onClose} />
      <div data-testid={`day-picker-${product.id}`} style={{ position: "fixed", top, left, width: 208 }}
        className="z-[101] rounded-2xl glass border border-border p-2 shadow-2xl">
        <p className="text-xs uppercase tracking-widest text-muted-foreground px-2 pt-1 pb-2 flex items-center gap-1.5"><MousePointerClick className="h-3.5 w-3.5 text-coral" /> Quels jours ?</p>
        <div className="space-y-1">
          {DAYS.map((d) => {
            const on = checked.has(d.id);
            return (
              <button key={d.id} data-testid={`day-check-${product.id}-${d.id}`} onClick={() => toggle(d.id)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${on ? "bg-coral/15 text-foreground ring-1 ring-coral" : "text-muted-foreground hover:bg-foreground/5"}`}>
                <span>{d.label}</span>
                <span className={`h-5 w-5 rounded-md border flex items-center justify-center ${on ? "bg-coral border-coral text-white" : "border-border"}`}>
                  {on && <Check className="h-3.5 w-3.5" />}
                </span>
              </button>
            );
          })}
        </div>
        <button data-testid={`day-confirm-${product.id}`} disabled={checked.size === 0} onClick={() => onConfirm([...checked])}
          className="mt-2 w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-heading text-sm font-semibold disabled:opacity-40 transition-colors">
          Confirmer{checked.size > 0 ? ` · ${checked.size} jour(s)` : ""}
        </button>
      </div>
    </>,
    document.body
  );
}

/* ============================ SHARED ============================ */
function Section({ n, title, hint, children }) {
  return (
    <section className="animate-fade-up">
      <div className="flex items-center gap-3 mb-4">
        {n && <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center shrink-0">{n}</span>}
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">{title}</h2>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Pill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-foreground/5 border border-border text-xs text-muted-foreground">
      <Icon className="h-3 w-3 text-coral" /> {children}
    </span>
  );
}

function StepBtn({ onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="h-11 w-11 flex items-center justify-center hover:bg-foreground/10 rounded-full text-foreground">
      {children}
    </button>
  );
}
